import { Button, Input, message, Modal, Switch } from 'antd';
import React from 'react';
import Icon from '../../../components/icon/Icon';
import { isUserAdmin } from '../../../helpers/CheckPermissionHelper';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import { EventsClient, MatchesClient, MatchDetailVm, SetMatchSyncLockRequest } from '../../../utils/api';
import { Props } from './index';
import CreateValidatorGame from './CreateValidatorGame';

type AdminEventAction = 'delete' | 'reimport';

interface State {
    adminAction?: AdminEventAction;
    eventCount?: number;
    countingEvents: boolean;
    confirmMatchId: string;
    submittingAdminAction: boolean;
    savingSyncLock: boolean;
    reimporting: boolean;
}

interface ReimportWatch {
    onCompleted?: () => void | Promise<void>;
    onIdle?: () => void;
}

const reimportWatches = new Map<number, ReimportWatch>();

function reimportMessageKey(matchId: number): string {
    return 'reimport-job-' + matchId;
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });
}

async function watchReimportJob(matchId: number, jobId: string): Promise<void> {
    const key = reimportMessageKey(matchId);
    const client = new MatchesClient();
    try {
        let job = await client.getReimportJob(matchId, jobId);
        while (job && (job.status === 'queued' || job.status === 'running')) {
            message.loading({
                content: job.status === 'running' ? 'Re-import running...' : 'Re-import queued...',
                key,
                duration: 0,
            });
            await delay(2000);
            job = await client.getReimportJob(matchId, jobId);
        }

        const watch = reimportWatches.get(matchId);
        if (job && job.status === 'succeeded') {
            message.success({
                content: `Imported ${job.eventsWritten} events from the source.`,
                key,
                duration: 4,
            });
        } else {
            message.error({
                content: (job && job.error) || 'The source import did not write events.',
                key,
                duration: 8,
            });
        }

        if (watch && watch.onCompleted) {
            await watch.onCompleted();
        }
    } catch (error) {
        message.error({
            content: 'Re-import status could not be read.',
            key,
            duration: 8,
        });
        errorWithUserMessage(error);
    } finally {
        const watch = reimportWatches.get(matchId);
        reimportWatches.delete(matchId);
        if (watch && watch.onIdle) {
            watch.onIdle();
        }
    }
}

function trackReimport(matchId: number, jobId: string, watch: ReimportWatch): void {
    const existing = reimportWatches.get(matchId);
    if (existing) {
        existing.onCompleted = watch.onCompleted;
        existing.onIdle = watch.onIdle;
        return;
    }

    reimportWatches.set(matchId, watch);
    void watchReimportJob(matchId, jobId);
}

class MatchAdminEventActions extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            countingEvents: false,
            confirmMatchId: '',
            submittingAdminAction: false,
            savingSyncLock: false,
            reimporting: false,
        };
    }

    private mounted = false;

    public componentDidMount(): void {
        this.mounted = true;
        this.attachWatch();
    }

    public componentDidUpdate(): void {
        this.attachWatch();
    }

    public componentWillUnmount(): void {
        this.mounted = false;
    }

    private attachWatch = (): void => {
        const watch = reimportWatches.get(this.props.match.id);
        if (!watch) {
            return;
        }

        watch.onCompleted = this.props.onCompleted;
        watch.onIdle = (): void => {
            if (this.mounted) {
                this.setState({ reimporting: false });
            }
        };
        if (!this.state.reimporting) {
            this.setState({ reimporting: true });
        }
    };

    private toggleSyncLock = async (locked: boolean): Promise<void> => {
        const { match, onCompleted } = this.props;
        this.setState({ savingSyncLock: true });
        try {
            await new MatchesClient().setSyncLock(
                match.id,
                new SetMatchSyncLockRequest({ locked })
            );
            success(locked ? 'Sync locked. Imports will skip this match.' : 'Sync unlocked.');
            if (onCompleted) {
                await onCompleted();
            }
        } catch (error) {
            errorWithUserMessage(error);
        } finally {
            this.setState({ savingSyncLock: false });
        }
    };

    private renderSyncLock = (): React.ReactElement => {
        const { match } = this.props;
        const { savingSyncLock } = this.state;
        return (
            <Switch
                checked={!!match.syncLocked}
                loading={savingSyncLock}
                onChange={(locked): void => {
                    void this.toggleSyncLock(locked);
                }}
            />
        );
    };

    private sourceLabel = (): string | undefined => {
        const { match } = this.props;
        const links = [
            match.hungaryGameId ? 'hungary' : '',
            match.eurocupMatchKey ? 'eurocup' : '',
            match.championsLeagueMatchKey ? 'champions' : '',
        ].filter(Boolean);
        if (links.length > 1) {
            return undefined;
        }
        if (match.hungaryGameId) {
            return `Hungary game ${match.hungaryGameId}`;
        }
        if (match.eurocupMatchKey) {
            return `Euro Cup ${match.eurocupMatchKey}`;
        }
        if (match.championsLeagueMatchKey) {
            return `Champions League ${match.championsLeagueMatchKey}`;
        }
        return undefined;
    };

    private openAdminAction = async (adminAction: AdminEventAction): Promise<void> => {
        const { match } = this.props;
        const sourceLinks = [
            match.hungaryGameId,
            match.eurocupMatchKey,
            match.championsLeagueMatchKey,
        ].filter(Boolean).length;
        if (adminAction === 'reimport' && sourceLinks > 1) {
            errorWithUserMessage(
                'This match is linked to more than one external source. Clear the extra link before re-importing.'
            );
            return;
        }

        this.setState({
            adminAction,
            eventCount: undefined,
            countingEvents: true,
            confirmMatchId: '',
        });

        try {
            const events = await new EventsClient().getLightByMatchId(match.id);
            this.setState({
                eventCount: events ? events.length : 0,
                countingEvents: false,
            });
        } catch (error) {
            this.setState({ countingEvents: false });
            errorWithUserMessage(error);
        }
    };

    private closeAdminAction = (): void => {
        this.setState({
            adminAction: undefined,
            confirmMatchId: '',
            submittingAdminAction: false,
        });
    };

    private submitAdminAction = async (): Promise<void> => {
        const { match, onCompleted } = this.props;
        const { adminAction, confirmMatchId } = this.state;
        if (confirmMatchId.trim() !== String(match.id) || !adminAction) {
            return;
        }

        this.setState({ submittingAdminAction: true });
        try {
            const client = new MatchesClient();
            if (adminAction === 'delete') {
                const result = await client.deleteEvents(match.id);
                success(`Removed ${result?.deletedCount ?? 0} events.`);
                this.closeAdminAction();
                if (onCompleted) {
                    await onCompleted();
                }
            } else {
                this.setState({ reimporting: true });
                const started = await client.startReimportJob(match.id);
                this.closeAdminAction();
                trackReimport(match.id, started.jobId, {
                    onCompleted,
                    onIdle: (): void => {
                        if (this.mounted) {
                            this.setState({ reimporting: false });
                        }
                    },
                });
            }
        } catch (error) {
            this.setState({ submittingAdminAction: false, reimporting: false });
            errorWithUserMessage(error);
            if (adminAction === 'delete' && onCompleted) {
                await onCompleted();
            }
        }
    };

    private renderModal = (
        match: MatchDetailVm,
        sourceLabel: string | undefined
    ): React.ReactElement => {
        const {
            adminAction,
            eventCount,
            countingEvents,
            confirmMatchId,
            submittingAdminAction,
        } = this.state;
        const confirmReady =
            !countingEvents &&
            eventCount !== undefined &&
            confirmMatchId.trim() === String(match.id);
        const eventCountText =
            eventCount === undefined
                ? 'the events'
                : eventCount === 1
                ? '1 event'
                : `${eventCount} events`;

        return (
            <Modal
                title={adminAction === 'reimport' ? 'Re-import from source' : 'Delete all events'}
                visible={!!adminAction}
                okText={adminAction === 'reimport' ? 'Re-import' : 'Delete events'}
                okButtonProps={{
                    disabled: !confirmReady,
                    danger: adminAction === 'delete',
                    loading: submittingAdminAction,
                }}
                cancelButtonProps={{ disabled: submittingAdminAction }}
                onOk={(): void => void this.submitAdminAction()}
                onCancel={this.closeAdminAction}
                destroyOnClose
            >
                {adminAction === 'reimport' ? (
                    <p>
                        This removes {eventCountText} from match {match.id}, then imports the
                        finished {sourceLabel} again. The match stays. A source lineup row that is
                        no longer mapped to that player is removed when it has no events. Players
                        you added by hand stay. The re-import keeps running after this window
                        closes, and a message shows when it finishes. If the import fails, the
                        match is left with no events and you can run this again.
                    </p>
                ) : (
                    <p>
                        This removes {eventCountText} from match {match.id}. The match, its
                        players, and its lineups stay.
                    </p>
                )}
                <p>Type {match.id} to confirm.</p>
                <Input
                    value={confirmMatchId}
                    onChange={(event): void =>
                        this.setState({ confirmMatchId: event.target.value })
                    }
                    onPressEnter={(): void => {
                        if (confirmReady) {
                            void this.submitAdminAction();
                        }
                    }}
                />
            </Modal>
        );
    };

    private renderMenuItems = (match: MatchDetailVm): React.ReactElement => {
        return (
            <>
                <div
                    className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center"
                    onClick={(): void => void this.openAdminAction('delete')}
                    role="button"
                >
                    <Icon name="icon-times" />
                    <span>Delete all events</span>
                </div>
                {(match.hungaryGameId || match.eurocupMatchKey || match.championsLeagueMatchKey) && (
                    <div
                        className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center"
                        onClick={(): void => {
                            if (!this.state.reimporting) {
                                void this.openAdminAction('reimport');
                            }
                        }}
                        role="button"
                    >
                        <Icon name="icon-exchange-alt" />
                        <span>{this.state.reimporting ? 'Re-import running' : 'Re-import from source'}</span>
                    </div>
                )}
                <div className="c-extended-menu__item u-flex u-flex-column u-align-items-center u-justify-content-center">
                    {this.renderSyncLock()}
                    <span>Lock sync</span>
                </div>
            </>
        );
    };

    private renderButtons = (match: MatchDetailVm): React.ReactElement => {
        return (
            <div style={{ margin: '8px 0 16px' }}>
                <Button danger onClick={(): void => void this.openAdminAction('delete')}>
                    Delete all events
                </Button>
                {(match.hungaryGameId || match.eurocupMatchKey || match.championsLeagueMatchKey) && (
                    <Button
                        type="primary"
                        style={{ marginLeft: 8 }}
                        loading={this.state.reimporting}
                        disabled={this.state.reimporting}
                        onClick={(): void => void this.openAdminAction('reimport')}
                    >
                        Re-import from source
                    </Button>
                )}
                <span style={{ marginLeft: 16 }}>
                    {this.renderSyncLock()}
                    <span style={{ marginLeft: 8 }}>Lock sync</span>
                </span>
            </div>
        );
    };

    public render(): React.ReactElement | null {
        const { match, userProfile, appearance } = this.props;
        if (!userProfile || !isUserAdmin(userProfile)) {
            return null;
        }

        const sourceLabel = this.sourceLabel();

        return (
            <>
                {appearance === 'menu' ? this.renderMenuItems(match) : this.renderButtons(match)}
                <CreateValidatorGame matchId={match.id} userProfile={userProfile} />
                {this.renderModal(match, sourceLabel)}
            </>
        );
    }
}

export default MatchAdminEventActions;
